/** Captura genérica de marcador (Eduardo 2026-09-15): hasta 5 sets, cada
 * uno con tie-break opcional (7-6) o como súper muerte a puntos. El formato
 * del Paso 5 ya no condiciona la captura (el backend acepta cualquier
 * cantidad de sets desde siempre): solo sugiere si el tercer set nace como
 * súper muerte. */

import type { TournamentMatchScoreSet } from "./api";

export const MIN_SCORE_SETS = 2;
export const MAX_SCORE_SETS = 5;

export type ScoreSetField = "p1" | "p2" | "tbWin" | "tbLose";

export interface ScoreSetDraft {
  /** Games (o puntos, en súper muerte) de cada lado. */
  p1: string;
  p2: string;
  /** Puntos del ganador/perdedor del tie-break de un set 7-6; se guarda
   * como "7-6(7-5)". Se autollenan entre sí (Eduardo 2026-08-28). */
  tbWin: string;
  tbLose: string;
  /** Súper muerte: la casilla es a puntos y se guarda como "TB 10-8". */
  superTb: boolean;
}

export interface ScoreDraft {
  sets: ScoreSetDraft[];
}

export function emptyScoreSet(superTb = false): ScoreSetDraft {
  return { p1: "", p2: "", tbWin: "", tbLose: "", superTb };
}

export function emptyScoreDraft(): ScoreDraft {
  return { sets: [emptyScoreSet(), emptyScoreSet()] };
}

export function cleanScoreNumber(value: string): string {
  return value.replace(/[^0-9]/g, "").slice(0, 2);
}

/** ¿El par de games es el 7-6 clásico? Solo informativo: desde Eduardo
 * 2026-09-24 el tie-break se captura en CUALQUIER set (hay organizadores
 * que juegan mini-sets a 4 y cierran 4-3 con tie-break). */
export function setPairHasTiebreak(a: string, b: string): boolean {
  return (a === "7" && b === "6") || (a === "6" && b === "7");
}

function padSets(sets: ScoreSetDraft[]): ScoreSetDraft[] {
  const out = sets.slice(0, MAX_SCORE_SETS);
  while (out.length < MIN_SCORE_SETS) out.push(emptyScoreSet());
  return out;
}

/** Mismo patrón que el backend: set a-b con tie-break opcional "(5)" o
 * "(7-5)" (se normaliza a puntos del perdedor); "TB 10-8" o cualquier lado
 * con 10+ puntos = súper muerte. Tokenizar por set — no por dígitos sueltos —
 * evita que el paréntesis descuadre los sets. En pickleball no hay súper muerte
 * ni tie-break (`pickleball`): 11-7 es un game normal, aun con «TB» heredado. */
export function scoreDraftFromText(scoreText: string | null | undefined, pickleball = false): ScoreDraft {
  const setPattern = /(TB\s*)?(\d+)\s*[-–:/]\s*(\d+)(?:\s*\(\s*(\d+)(?:\s*[-–:/]\s*(\d+))?\s*\))?/gi;
  const found = [...(scoreText ?? "").matchAll(setPattern)];
  const sets = found.map((entry) => {
    const p1 = entry[2];
    const p2 = entry[3];
    const superTb = !pickleball && (Boolean(entry[1]) || Number(p1) >= 10 || Number(p2) >= 10);
    let tbLose = "";
    let tbWin = "";
    if (!pickleball && !superTb && entry[4] !== undefined) {
      if (entry[5] !== undefined) {
        tbLose = String(Math.min(Number(entry[4]), Number(entry[5])));
        tbWin = String(Math.max(Number(entry[4]), Number(entry[5])));
      } else {
        tbLose = entry[4];
        const loser = Number(entry[4]);
        tbWin = Number.isFinite(loser) ? String(Math.max(7, loser + 2)) : "";
      }
    }
    return { p1, p2, tbWin, tbLose, superTb };
  });
  return { sets: padSets(sets) };
}

/** Borrador desde un partido guardado: el texto crudo manda (conserva el
 * marcador completo del TB y el prefijo de súper muerte); sin texto, los
 * sets estructurados. */
export function scoreDraftFromSets(
  structured: TournamentMatchScoreSet[] | null | undefined,
  scoreText: string | null | undefined,
  pickleball = false,
): ScoreDraft {
  const fromText = scoreDraftFromText(scoreText, pickleball);
  if (fromText.sets.some((set) => set.p1 || set.p2)) return fromText;
  const sorted = [...(structured ?? [])].sort((a, b) => a.set_number - b.set_number);
  if (sorted.length === 0) return fromText;
  return {
    sets: padSets(
      sorted.map((set) => {
        const superTb = !pickleball && Boolean(set.is_super_tiebreak);
        const tbLose = !pickleball && !superTb && set.tiebreak_points != null ? String(set.tiebreak_points) : "";
        return {
          p1: set.player1_games?.toString() ?? "",
          p2: set.player2_games?.toString() ?? "",
          tbLose,
          tbWin: tbLose ? String(Math.max(7, Number(tbLose) + 2)) : "",
          superTb,
        };
      }),
    ),
  };
}

export function scoreDraftHasAnyValue(draft: ScoreDraft | undefined): boolean {
  return Boolean(draft && draft.sets.some((set) => set.p1.trim() || set.p2.trim() || set.tbWin.trim() || set.tbLose.trim()));
}

function tiebreakSuffix(set: ScoreSetDraft): string {
  // El tie-break vale en cualquier set (mini-sets 4-3, 5-4…), no solo 7-6.
  if (set.superTb) return "";
  const l = set.tbLose.trim();
  const w = set.tbWin.trim();
  if (l && w) return `(${w}-${l})`;
  if (l) return `(${l})`;
  // Solo ganador: con w >= 8 el perdedor es exacto (w-2); con 7 es ambiguo.
  if (w && Number(w) >= 8) return `(${w}-${Number(w) - 2})`;
  return "";
}

/** Texto canónico "6-4 7-6(7-5) TB 10-8"; los sets vacíos se omiten. */
export function scoreTextFromDraft(draft: ScoreDraft): string {
  return draft.sets
    .filter((set) => set.p1 || set.p2)
    .map((set) => `${set.superTb ? "TB " : ""}${set.p1}-${set.p2}${tiebreakSuffix(set)}`)
    .join(" ")
    .trim();
}

/** Actualiza un campo de un set con el autollenado del tie-break: capturar
 * un lado sugiere el otro (perdedor → ganador = max(7, perdedor+2); ganador
 * >= 8 → perdedor = ganador-2), sin pisar lo ya tecleado. */
export function updateScoreSet(draft: ScoreDraft, index: number, field: ScoreSetField, rawValue: string): ScoreDraft {
  const value = cleanScoreNumber(rawValue);
  const sets = draft.sets.map((set) => ({ ...set }));
  const set = sets[index];
  if (!set) return draft;
  set[field] = value;
  if (value && field === "tbLose" && !set.tbWin) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) set.tbWin = String(Math.max(7, numeric + 2));
  }
  if (value && field === "tbWin" && !set.tbLose) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 8) set.tbLose = String(numeric - 2);
  }
  return { sets };
}

export function toggleScoreSetSuperTb(draft: ScoreDraft, index: number): ScoreDraft {
  const sets = draft.sets.map((set, i) => (i === index ? { ...set, superTb: !set.superTb, tbWin: "", tbLose: "" } : set));
  return { sets };
}

export function addScoreSet(draft: ScoreDraft, superTb = false): ScoreDraft {
  if (draft.sets.length >= MAX_SCORE_SETS) return draft;
  return { sets: [...draft.sets, emptyScoreSet(superTb)] };
}

export function removeLastScoreSet(draft: ScoreDraft): ScoreDraft {
  if (draft.sets.length <= MIN_SCORE_SETS) return draft;
  return { sets: draft.sets.slice(0, -1) };
}

/** Etiqueta de la casilla: "Set 3", "Súper muerte" o "Game 3" en pickleball. */
export function scoreSetDraftLabel(index: number, set: ScoreSetDraft, pickleball: boolean): string {
  if (pickleball) return `Game ${index + 1}`;
  return set.superTb ? "Súper muerte" : `Set ${index + 1}`;
}
